import { useEffect } from 'react';
import {
  ReportIssueDialog,
  type ReportIssueSubmitInput,
} from '@ampersandbrands/ui';
import {
  COCKPIT_TOOL_OPTIONS,
  type SupportScreenshot,
  useCreateSupportIssue,
} from '@/lib/supportIssue';
import type { ReportPageStateEntry } from '@/lib/reportPageState';

type Props = {
  open: boolean;
  initialTool?: string;
  route: string;
  screenshot?: SupportScreenshot | null;
  screenshotAttempted: boolean;
  /**
   * Human-readable page state captured when the report dialog was opened
   * (Layout.tsx reads `getReportPageState()` at click time). Sent as
   * `page_state` when the calling page published anything; omitted
   * otherwise, which leaves the api on its existing URL-parsing fallback.
   */
  pageState?: ReportPageStateEntry[];
  onClose: () => void;
};

export function ReportIssueModal({
  open,
  initialTool,
  route,
  screenshot,
  screenshotAttempted,
  pageState,
  onClose,
}: Props) {
  const createIssue = useCreateSupportIssue();

  useEffect(() => {
    if (open) createIssue.reset();
    // Reset only when a new modal session opens; including the full mutation
    // object here causes success/error state to clear during normal rerenders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const submit = (input: ReportIssueSubmitInput) => {
    createIssue.mutate({
      product: 'cockpit',
      kind: input.kind,
      tool: input.tool,
      summary: input.summary,
      description: input.description,
      description_html: input.description_html,
      priority: input.priority,
      route: input.route,
      url: window.location.href,
      page_title: document.title,
      page_state: pageState && pageState.length > 0 ? pageState : undefined,
      // `screenshot` stays on the payload while the api still reads it, so a
      // report filed mid-deploy keeps its image either way.
      screenshot: input.screenshot,
      attachments: input.attachments,
      client_context: {
        timestamp: new Date().toISOString(),
        user_agent: window.navigator.userAgent,
      },
    });
  };

  return (
    <ReportIssueDialog
      productName="Cockpit"
      toolLabel="Tool"
      toolOptions={COCKPIT_TOOL_OPTIONS}
      initialTool={initialTool}
      route={route}
      screenshot={screenshot ?? null}
      screenshotAttempted={screenshotAttempted}
      isSubmitting={createIssue.isPending}
      error={createIssue.error?.message ?? null}
      ticket={createIssue.data?.ticket ?? null}
      onSubmit={submit}
      onClose={onClose}
    />
  );
}
